import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Select,
  TextField,
  Button,
  MenuItem,
  useMediaQuery,
  Theme,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Search as SearchIcon,
  //   Menu as MenuIcon,
  //   Refresh as RefreshIcon,
  SupportAgent as SupportAgentIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  UnfoldMore as UnfoldMoreIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import SideMenu from '../components/SideMenu';
import { fetchApi } from '../services/api';
import CounselingDetailDialog from '../components/CounselingDetailDialog';
import AssignInfoDialog from '../components/AssignInfoDialog';
// import { convertFieldResponseIntoMuiTextFieldProps } from "@mui/x-date-pickers/internals";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ko } from 'date-fns/locale';
import { styled } from '@mui/material/styles';
import { COLOR_SETS } from '../theme/colorSets';
import DatePickerWrapper from '../components/DatePickerWrapper';
import * as XLSX from 'xlsx';

interface CounselingData {
  상담번호: string;
  상담구분: string;
  담당자: string;
  담당자전화: string;
  담당자이메일: string;
  판매업체번호: number;
  요청자명: string;
  견적명: string;
  수배처: string;
  수배처번호: number;
  상담상태: string;
  출발일: string;
  종료일: string;
  등록일: string;
  경로: string;
  판매총액: number;
  원가총액: number;
  예상총수익: number;
  입금총액: number;
  출금총액: number;
  총수익: number;
  인원수: string;
  입출금: string | any[];
  판매업체: string;
  계약금여부: boolean;
  완납여부: boolean;
  확정서여부: boolean;
  설명자료여부: boolean;
  해피콜여부: boolean;
  정산마감여부: boolean;
}

interface Column {
  id: string;
  label: string;
  render?: (row: CounselingData) => string | React.ReactNode;
  sortable?: boolean;
  filterable?: boolean;
  width: string;
  minWidth?: string;
  maxWidth?: string;
  align?: 'left' | 'right';
}

type ApiResponse<T> = {
  table: T[];
};

type SortDirection = 'asc' | 'desc' | null;

const drawerWidth = 240;

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    height: '32px',
    fontSize: '14px',
    backgroundColor: '#fff',
    '&:hover fieldset': {
      borderColor: '#1976d2',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#1976d2',
    },
    '&.Mui-disabled': {
      backgroundColor: '#f5f5f5',
    },
  },
});

const CounselingCard = ({
  counseling,
  viewType,
}: {
  counseling: CounselingData;
  viewType: 'counseling' | 'profit';
}) => {
  const formatNumber = (num: number) => num.toLocaleString('ko-KR');

  if (viewType === 'profit') {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent>
          <Typography variant="subtitle1" component="div" noWrap sx={{ mb: 1.5, fontWeight: 'bold' }}>
            {counseling.견적명}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                판매총액:
              </Typography>
              <Typography variant="body2">{formatNumber(counseling.판매총액)}원</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                원가총액:
              </Typography>
              <Typography variant="body2">{formatNumber(counseling.원가총액)}원</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                예상수익:
              </Typography>
              <Typography variant="body2">{formatNumber(counseling.예상총수익)}원</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                미수:
              </Typography>
              <Typography variant="body2">{formatNumber(counseling.판매총액 - counseling.입금총액)}원</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                미지급:
              </Typography>
              <Typography variant="body2">{formatNumber(counseling.원가총액 - counseling.출금총액)}원</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                총수익:
              </Typography>
              <Typography variant="body2" color="primary.main" sx={{ fontWeight: 'bold' }}>
                {formatNumber(counseling.총수익)}원
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent>
        <Typography variant="subtitle1" component="div" noWrap sx={{ mb: 1.5, fontWeight: 'bold' }}>
          {counseling.견적명}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <Typography
              variant="body2"
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                fontSize: '0.75rem',
              }}>
              {counseling.상담상태}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              고객명:
            </Typography>
            <Typography variant="body2">{counseling.요청자명}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              여행기간:
            </Typography>
            <Typography variant="body2">
              {counseling.출발일} ~ {counseling.종료일}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              등록일시:
            </Typography>
            <Typography variant="body2">{counseling.등록일}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              경로:
            </Typography>
            <Typography variant="body2">{counseling.경로}</Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const CounselingListPage = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [counselings, setCounselings] = useState<CounselingData[]>([]);
  const [selectedCounseling, setSelectedCounseling] = useState<string>('');
  const [dialogVisible, setDialogVisible] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [currentCounselingData, setCurrentCounselingData] = useState<CounselingData | null>(null);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState<Dayjs>(dayjs().subtract(30, 'day'));
  const [endDate, setEndDate] = useState<Dayjs>(dayjs());
  const [searchType, setSearchType] = useState('등록일');
  const [status, setStatus] = useState('1');
  const [viewType, setViewType] = useState<'counseling' | 'profit'>('counseling');
  const [sortConfig, setSortConfig] = useState<{
    field: string;
    direction: SortDirection;
  }>({
    field: '',
    direction: null,
  });
  const [filters, setFilters] = useState<{ [key: string]: string }>({});
  const [scrollPosition, setScrollPosition] = useState(0);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    dayjs().subtract(30, 'day').toDate(),
    dayjs().toDate(),
  ]);
  const [searchTerm, setSearchTerm] = useState('');
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const columns: Column[] = [
    {
      id: '견적명',
      label: '견적명 / 상품명',
      sortable: true,
      filterable: true,
      width: '480px',
      minWidth: '380px',
    },
    {
      id: '인원수',
      label: '인원',
      sortable: true,
      filterable: true,
      width: '50px',
    },
    {
      id: '여행기간',
      label: '여행기간',
      render: (row: CounselingData) => `${row.출발일} ~ ${row.종료일}`,
      sortable: true,
      width: '90px',
      maxWidth: '350px',
    },
    {
      id: '수배처',
      label: '수배',
      sortable: true,
      filterable: true,
      width: '110px',
      maxWidth: '130px',
    },
    {
      id: '판매업체',
      label: '판매',
      sortable: true,
      filterable: true,
      width: '110px',
      maxWidth: '130px',
    },
    {
      id: '요청자명',
      label: '고객명',
      sortable: true,
      filterable: true,
      width: '60px',
    },
    {
      id: '상담상태',
      label: '상태',
      sortable: true,
      filterable: true,
      width: '60px',
    },
    {
      id: '계약금여부',
      label: '계약금',
      sortable: true,
      width: '55px',
      render: (row: CounselingData) => (
        <Box sx={{ textAlign: 'center', px: 0 }}>
          {row.계약금여부 ? '✓' : ''}
        </Box>
      )
    },
    {
      id: '완납여부',
      label: '완납',
      sortable: true,
      width: '45px',
      render: (row: CounselingData) => (
        <Box sx={{ textAlign: 'center', px: 0 }}>
          {row.완납여부 ? '✓' : ''}
        </Box>
      )
    },
    {
      id: '확정서여부',
      label: '확정서',
      sortable: true,
      width: '55px',
      render: (row: CounselingData) => (
        <Box sx={{ textAlign: 'center', px: 0 }}>
          {row.확정서여부 ? '✓' : ''}
        </Box>
      )
    },
    {
      id: '설명자료여부',
      label: '설명자료',
      sortable: true,
      width: '65px',
      render: (row: CounselingData) => (
        <Box sx={{ textAlign: 'center', px: 0 }}>
          {row.설명자료여부 ? '✓' : ''}
        </Box>
      )
    },
    {
      id: '해피콜여부',
      label: '해피콜',
      sortable: true,
      width: '55px',
      render: (row: CounselingData) => (
        <Box sx={{ textAlign: 'center', px: 0 }}>
          {row.해피콜여부 ? '✓' : ''}
        </Box>
      )
    },
    {
      id: '정산마감여부',
      label: '정산',
      sortable: true,
      width: '45px',
      render: (row: CounselingData) => (
        <Box sx={{ textAlign: 'center', px: 0 }}>
          {row.정산마감여부 ? '✓' : ''}
        </Box>
      )
    },
    {
      id: '등록일',
      label: '등록일시',
      sortable: true,
      width: '80px',
      maxWidth: '100px',
    },
    {
      id: '경로',
      label: '경로',
      sortable: true,
      filterable: true,
      width: '70px',
      maxWidth: '70px',
    },
  ];

  const columnsProfit: Column[] = [
    {
      id: '견적명',
      label: '견적명 / 상품명',
      sortable: true,
      filterable: true,
      width: '300px',
      minWidth: '300px',
    },
    {
      id: '인원수',
      label: '인원',
      sortable: true,
      filterable: true,
      width: '80px',
    },
    {
      id: '여행기간',
      label: '여행기간',
      render: (row: CounselingData) => `${row.출발일} ~ ${row.종료일}`,
      sortable: true,
      width: '180px',
      maxWidth: '180px',
    },
    {
      id: '수배처',
      label: '수배업체',
      sortable: true,
      filterable: true,
      width: '150px',
    },
    {
      id: '판매업체',
      label: '판매업체',
      sortable: true,
      filterable: true,
      width: '150px',
    },
    {
      id: '상담상태',
      label: '상태',
      sortable: true,
      filterable: true,
      width: '100px',
    },
    {
      id: '판매총액',
      label: '판매총액',
      render: (row: CounselingData) => `${row.판매총액.toLocaleString('ko-KR')}`,
      sortable: true,
      width: '100px',
      align: 'right',
    },
    {
      id: '원가총액',
      label: '원가총액',
      render: (row: CounselingData) => `${row.원가총액.toLocaleString('ko-KR')}`,
      sortable: true,
      width: '100px',
      align: 'right',
    },
    {
      id: '예상총수익',
      label: '예상수익',
      render: (row: CounselingData) => `${row.예상총수익.toLocaleString('ko-KR')}`,
      sortable: true,
      width: '100px',
      align: 'right',
    },
    {
      id: '미수',
      label: '미수',
      render: (row: CounselingData) => `${(row.판매총액 - row.입금총액).toLocaleString('ko-KR')}`,
      sortable: true,
      width: '100px',
      align: 'right',
    },
    {
      id: '미지급',
      label: '미지급',
      render: (row: CounselingData) => `${(row.원가총액 - row.출금총액).toLocaleString('ko-KR')}`,
      sortable: true,
      width: '100px',
      align: 'right',
    },
    {
      id: '총수익',
      label: '총수익',
      render: (row: CounselingData) => `${row.총수익.toLocaleString('ko-KR')}`,
      sortable: true,
      width: '100px',
      align: 'right',
    },
  ];

  const handleSort = (field: string) => {
    setSortConfig((prevConfig) => ({
      field,
      direction:
        prevConfig.field === field
          ? prevConfig.direction === 'asc'
            ? 'desc'
            : prevConfig.direction === 'desc'
            ? null
            : 'asc'
          : 'asc',
    }));
  };

  const getSortIcon = (field: string) => {
    if (sortConfig.field !== field) return <UnfoldMoreIcon fontSize="small" sx={{ opacity: 0.3 }} />;
    if (sortConfig.direction === 'asc') return <ArrowUpwardIcon fontSize="small" />;
    if (sortConfig.direction === 'desc') return <ArrowDownwardIcon fontSize="small" />;
    return <UnfoldMoreIcon fontSize="small" sx={{ opacity: 0.3 }} />;
  };

  const filteredAndSortedData = useMemo(() => {
    let result = [...counselings];

    // 검색어로 모든 필드 검색
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter((item) => {
        return Object.values(item).some((value) => String(value).toLowerCase().includes(searchLower));
      });
    }

    // 정렬 적용
    if (sortConfig.field && sortConfig.direction) {
      result.sort((a, b) => {
        const aValue = a[sortConfig.field as keyof CounselingData];
        const bValue = b[sortConfig.field as keyof CounselingData];

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }

        const aString = String(aValue).toLowerCase();
        const bString = String(bValue).toLowerCase();

        return sortConfig.direction === 'asc' ? aString.localeCompare(bString) : bString.localeCompare(aString);
      });
    }

    return result;
  }, [counselings, searchTerm, sortConfig]);

  const calculateTotals = useMemo(() => {
    return filteredAndSortedData.reduce(
      (acc, row) => ({
        판매총액: acc.판매총액 + row.판매총액,
        원가총액: acc.원가총액 + row.원가총액,
        예상총수익: acc.예상총수익 + row.예상총수익,
        미수: acc.미수 + (row.판매총액 - row.입금총액),
        미지급: acc.미지급 + (row.원가총액 - row.출금총액),
        총수익: acc.총수익 + row.총수익,
      }),
      {
        판매총액: 0,
        원가총액: 0,
        예상총수익: 0,
        미수: 0,
        미지급: 0,
        총수익: 0,
      }
    );
  }, [filteredAndSortedData]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const fetchCounselingList = async () => {
    setLoading(true);
    try {
      const currentScroll = window.scrollY;
      setScrollPosition(currentScroll);

      const postData = [
        { 컬럼명: '@업체번호', 값: localStorage.getItem('CompanyNo') || '' },
        { 컬럼명: '@시작일', 값: startDate.format('YYYY-MM-DD') },
        { 컬럼명: '@종료일', 값: endDate.format('YYYY-MM-DD') },
        { 컬럼명: '@구분', 값: searchType },
        { 컬럼명: '@상태', 값: status },
        { 컬럼명: '@유저번호', 값: '12' },
      ];

      const response = await fetchApi<ApiResponse<CounselingData>>('DBO.ITN_S_상담리스트', postData);
      if (response) {
        setCounselings(response.data.table || []);
        // 데이터 로드 후 스크롤 위치 복원
        setTimeout(() => {
          window.scrollTo({
            top: currentScroll,
            behavior: 'auto',
          });
        }, 100);
      }
    } catch (error) {
      console.error('상담 리스트 조회 실패:', error);
      setCounselings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (record?: CounselingData) => {
    if (!record) {
      setSelectedCounseling('0');
      setDialogVisible(true);
      return;
    }
    setSelectedCounseling(record.상담번호.toString());
    setDialogVisible(true);
  };

  const handleCompanyClick = (event: React.MouseEvent, record: CounselingData) => {
    event.stopPropagation(); // Prevent row click
    const companyNo = Number(localStorage.getItem('CompanyNo'));
    if (record.판매업체번호 !== companyNo || record.수배처번호 !== companyNo) {
      setCurrentCounselingData(record);
      setShowAssignDialog(true);
    }
  };

  const handleExcelDownload = () => {
    // 상담리스트 워크시트 데이터 준비
    const excelData = filteredAndSortedData.map((item) => ({
      '견적명/상품명': item.견적명,
      인원: item.인원수,
      여행기간: `${item.출발일} ~ ${item.종료일}`,
      수배업체: item.수배처,
      판매업체: item.판매업체,
      상태: item.상담상태,
      고객명: item.요청자명,
      등록일시: item.등록일,
      경로: item.경로,
      ...(viewType === 'profit'
        ? {
            판매총액: item.판매총액,
            원가총액: item.원가총액,
            예상수익: item.예상총수익,
            미수: item.판매총액 - item.입금총액,
            미지급: item.원가총액 - item.출금총액,
            총수익: item.총수익,
          }
        : {}),
    }));

    // 합계 행 추가
    if (viewType === 'profit') {
      excelData.push({
        '견적명/상품명': '합계',
        인원: '',
        여행기간: '',
        수배업체: '',
        판매업체: '',
        상태: '',
        고객명: '',
        등록일시: '',
        경로: '',
        판매총액: calculateTotals.판매총액,
        원가총액: calculateTotals.원가총액,
        예상수익: calculateTotals.예상총수익,
        미수: calculateTotals.미수,
        미지급: calculateTotals.미지급,
        총수익: calculateTotals.총수익,
      });
    }

    // 입출금 워크시트 데이터 준비
    const paymentData: any[] = [];
    filteredAndSortedData.forEach((item) => {
      if (item.입출금) {
        try {
          // 이미 배열인 경우와 문자열인 경우를 모두 처리
          const payments = typeof item.입출금 === 'string' ? JSON.parse(item.입출금) : item.입출금;

          if (Array.isArray(payments)) {
            payments.forEach((payment: any) => {
              paymentData.push({
                '견적명/상품명': item.견적명,
                인원: item.인원수,
                여행기간: `${item.출발일} ~ ${item.종료일}`,
                입출금번호: payment.입출금번호,
                일시: payment.일시,
                구분: payment.구분,
                입출대상: payment.입출대상,
                내역: payment.내역,
                금액: payment.금액,
                정산번호: payment.정산번호,
              });
            });
          }
        } catch (error) {
          console.error('입출금 데이터 파싱 오류:', error);
        }
      }
    });

    // 워크북 생성
    const wb = XLSX.utils.book_new();

    // 상담리스트 워크시트 추가
    const ws1 = XLSX.utils.json_to_sheet(excelData);
    XLSX.utils.book_append_sheet(wb, ws1, '상담리스트');

    // 입출금 워크시트 추가
    const ws2 = XLSX.utils.json_to_sheet(paymentData);
    XLSX.utils.book_append_sheet(wb, ws2, '입출금');

    // 파일 저장
    const fileName = `상담리스트_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  useEffect(() => {
    setSearchTerm(''); // Clear search term when view type changes
    // handleRefresh();
    fetchCounselingList();
  }, [viewType]);

  const getThemeBackgroundColor = () => {
    const savedTheme = localStorage.getItem('selectedTheme') || 'modern';
    const theme = COLOR_SETS.find((set) => set.id === savedTheme);

    return theme?.palette.background?.default || '#E8EAF6';
  };

  const checkIsOther = (row: CounselingData) => {
    const companyNo = Number(localStorage.getItem('CompanyNo'));

    return companyNo !== row.판매업체번호 && companyNo === row.수배처번호;
  };

  const renderPCContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <>
        <Paper
          elevation={0}
          sx={{
            ...(isMobile ? { p: 1.5, mt: 10 } : { p: 1.5, m: 1.5, mt: 0 }),

            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#fff',
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            height: '62px',
          }}>
          <Box
            sx={{
              mb: 0,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SupportAgentIcon sx={{ color: 'primary.main' }} />
              <Typography variant="h6" sx={{ pr: 2 }}>
                상담 관리
              </Typography>
              <ToggleButtonGroup
                value={viewType}
                exclusive
                onChange={(e, newValue) => {
                  if (newValue !== null) {
                    setViewType(newValue);
                  }
                }}
                size="small"
                aria-label="view type">
                <ToggleButton value="counseling" aria-label="counseling management">
                  상담용
                </ToggleButton>
                <ToggleButton value="profit" aria-label="profit management">
                  수익용
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                size="small"
                sx={{ height: '32px', width: '120px', fontsize: '14px' }}>
                <MenuItem value="1">예약확정/진행중</MenuItem>
                <MenuItem value="2">상담취소</MenuItem>
                {/* <MenuItem value="4">전체</MenuItem> */}
              </Select>

              <Select
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
                size="small"
                sx={{ height: '32px', width: '100px', fontsize: '14px' }}>
                <MenuItem value="등록일">상담 작성일</MenuItem>
                <MenuItem value="출발일">여행 시작일</MenuItem>
              </Select>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <DatePickerWrapper>
                  <DatePicker
                    selectsRange
                    monthsShown={2}
                    startDate={dateRange[0]}
                    endDate={dateRange[1]}
                    onChange={(update: [Date | null, Date | null]) => {
                      setDateRange(update);
                      const [start, end] = update;
                      if (start) setStartDate(dayjs(start));
                      if (end) setEndDate(dayjs(end));
                      if (end) {
                        setTimeout(() => {
                          const element = document.querySelector('.react-datepicker__input-container input');
                          if (element) {
                            (element as HTMLElement).blur();
                          }
                        }, 100);
                      }
                    }}
                    dateFormat="yyyy/MM/dd"
                    locale={ko}
                    placeholderText="시작/종료일을 선택하세요"
                    isClearable
                    showPopperArrow={false}
                    calendarStartDay={1}
                    customInput={
                      <StyledTextField
                        fullWidth
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            height: '32px',
                            width: '320px',
                          },
                        }}
                      />
                    }
                  />
                </DatePickerWrapper>
              </Box>
              <IconButton
                onClick={fetchCounselingList}
                color="primary"
                size="small"
                sx={{
                  px: 3,
                  py: 0,
                  mr: 1,
                  fontsize: '16px',
                  borderColor: 'primary.main',
                  border: '1px solid',
                  borderRadius: '4px',
                }}>
                <SearchIcon sx={{ height: '30px' }} />
                <Typography sx={{ fontSize: '14px', fontWeight: 'bold' }}>검색</Typography>
              </IconButton>

              <Button
                variant="contained"
                sx={{
                  width: '120px',
                  height: '36px',
                  borderRadius: '20px',
                  backgroundColor: 'transparent',
                  color: 'primary.main',
                  ml: 2,
                  p: 0,
                }}
                onClick={(e: React.MouseEvent) => handleRowClick()}>
                <AddIcon sx={{ color: 'primary.main' }} />
                상담등록
              </Button>
            </Box>
          </Box>
        </Paper>
        <Paper
          elevation={1}
          sx={{
            p: 1.5,
            m: 1.5,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#fff',
            minHeight: 'calc(100vh - 88px)',
          }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      backgroundColor: 'background.paper',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      width: '300px',
                      minWidth: '300px',
                    }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        // gap: 2,
                        justifyContent: 'space-between',
                        // pr: 2,
                      }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          // gap: 0.5,
                          cursor: 'pointer',
                        }}
                        onClick={() => handleSort('견적명')}>
                        견적명 / 상품명
                        {getSortIcon('견적명')}
                      </Box>
                      <TextField
                        size="small"
                        placeholder="검색어 입력"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            height: '32px',
                            fontSize: '14px',
                            backgroundColor: 'white',
                            width: '180px',
                          },
                        }}
                      />
                    </Box>
                  </TableCell>

                  {(viewType === 'counseling' ? columns : columnsProfit).slice(1).map((column) => (
                    <TableCell
                      key={column.id}
                      sx={{
                        backgroundColor: 'background.paper',
                        fontWeight: 'bold',
                        cursor: column.sortable ? 'pointer' : 'default',
                        width: column.width,
                        minWidth: column.minWidth,
                        maxWidth: column.maxWidth,
                        textAlign: column.align || 'left',
                        padding: '6px 0px',
                      }}
                      onClick={() => column.sortable && handleSort(column.id)}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}>
                        {column.label}
                        {column.sortable && getSortIcon(column.id)}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  {(viewType === 'counseling' ? columns : columnsProfit).map((column) => (
                    <TableCell
                      key={`filter-${column.id}`}
                      sx={{
                        padding: '4px',
                        backgroundColor: '#f5f5f5',
                        width: column.width,
                        height: '36px',
                        display: viewType !== 'profit' ? 'none' : '',
                      }}>
                      {viewType === 'profit' &&
                        (column.id === '견적명' ? (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleExcelDownload}
                            sx={{ ml: 1, fontSize: '12px', height: '32px', width: '100px' }}>
                            엑셀 다운로드
                          </Button>
                        ) : (
                          !column.filterable && (
                            <Box
                              sx={{
                                textAlign: 'right',
                                fontWeight: 'bold',
                                color: 'primary.main',
                                padding: '4px',
                                fontSize: '12px',
                                pr: 1.5,
                              }}>
                              {column.id === '판매총액' && calculateTotals.판매총액.toLocaleString('ko-KR')}
                              {column.id === '원가총액' && calculateTotals.원가총액.toLocaleString('ko-KR')}
                              {column.id === '예상총수익' && calculateTotals.예상총수익.toLocaleString('ko-KR')}
                              {column.id === '미수' && calculateTotals.미수.toLocaleString('ko-KR')}
                              {column.id === '미지급' && calculateTotals.미지급.toLocaleString('ko-KR')}
                              {column.id === '총수익' && calculateTotals.총수익.toLocaleString('ko-KR')}
                            </Box>
                          )
                        ))}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAndSortedData.map((row) => (
                  <TableRow
                    key={row.상담번호}
                    onClick={() => handleRowClick(row)}
                    hover
                    sx={{
                      height: '52px',
                      cursor: 'pointer',
                      position: 'relative',
                      '&:hover': {
                        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                        '& .MuiTableCell-root': {
                          backgroundColor: 'transparent',
                        },
                      },
                    }}>
                    {(viewType === 'counseling' ? columns : columnsProfit).map((column) => {
                      const companyNo = Number(localStorage.getItem('CompanyNo'));
                      const isDifferentCompany =
                        (column.id === '수배처' && row.수배처번호 && row.수배처번호 !== companyNo) ||
                        (column.id === '판매업체' && row.판매업체번호 !== companyNo);
                      const isCompanyColumn = column.id === '수배처' || column.id === '판매업체';

                      return (
                        <TableCell
                          key={column.id}
                          onClick={
                            isCompanyColumn && row[column.id === '수배처' ? '수배처번호' : '판매업체번호']
                              ? (e) => handleCompanyClick(e, row)
                              : undefined
                          }
                          sx={{
                            padding: '6px 6px',
                            ...(isCompanyColumn && row[column.id === '수배처' ? '수배처번호' : '판매업체번호']
                              ? {
                                  cursor: 'pointer',
                                  '&:hover': {
                                    textDecoration: 'underline',
                                  },
                                }
                              : undefined),
                          }}>
                          <Box
                            sx={{
                              textAlign: column.align || 'left',
                              color: isDifferentCompany ? 'primary.main' : 'inherit',
                              fontWeight: isDifferentCompany ? 'bold' : 'normal',
                            }}>
                            {column.render ? column.render(row) : row[column.id as keyof CounselingData]}
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </>
    );
  };

  const renderMobileContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ py: 2 }}>
        <Paper elevation={0} sx={{ mb: 2, p: 2, mt: 4, backgroundColor: '#fff' }}>
          {/* First Row */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{ whiteSpace: 'nowrap' }}>
                상담 관리
              </Typography>
              <ToggleButtonGroup
                value={viewType}
                exclusive
                onChange={(e, newValue) => {
                  if (newValue !== null) {
                    setViewType(newValue);
                  }
                }}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    px: 1.5,
                    py: 0.5,
                    backgroundColor: '#fff',
                  },
                }}>
                <ToggleButton value="counseling">상담용</ToggleButton>
                <ToggleButton value="profit">수익용</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Button
              variant="contained"
              onClick={(e: React.MouseEvent) => handleRowClick()}
              sx={{
                borderRadius: '20px',
                whiteSpace: 'nowrap',
                backgroundColor: '#fff',
                color: 'primary.main',
                border: '1px solid',
                borderColor: 'primary.main',
                height: '36px',
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: '#fff',
                },
              }}>
              <AddIcon />
              상담등록
            </Button>
          </Box>

          {/* Second Row - Combined filters in one line */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              '& .MuiSelect-select, & .MuiInputBase-input': {
                fontSize: '12px',
                py: 0.5,
                px: 1,
              },
            }}>
            <DatePickerWrapper sx={{ flex: 1 }}>
              <DatePicker
                selectsRange
                monthsShown={1}
                startDate={dateRange[0]}
                endDate={dateRange[1]}
                onChange={(update: [Date | null, Date | null]) => {
                  setDateRange(update);
                  const [start, end] = update;
                  if (start) setStartDate(dayjs(start));
                  if (end) setEndDate(dayjs(end));
                  if (end) {
                    setTimeout(() => {
                      const element = document.querySelector('.react-datepicker__input-container input');
                      if (element) {
                        (element as HTMLElement).blur();
                      }
                    }, 100);
                  }
                }}
                dateFormat="yyyy/MM/dd"
                locale={ko}
                placeholderText="시작/종료일을 선택하세요"
                isClearable
                showPopperArrow={false}
                calendarStartDay={1}
                customInput={
                  <StyledTextField
                    fullWidth
                    size="small"
                    sx={{
                      backgroundColor: '#fff',
                      '& .MuiInputBase-input': {
                        fontSize: '12px',
                        py: 0.5,
                        px: 1,
                        height: '18px',
                      },
                    }}
                  />
                }
              />
            </DatePickerWrapper>

            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              size="small"
              sx={{
                minWidth: 70,
                backgroundColor: '#fff',
                height: '30px',
              }}>
              <MenuItem value="1">예약확정/진행중</MenuItem>
              <MenuItem value="2">상담취소</MenuItem>
              {/* <MenuItem value="4">전체</MenuItem> */}
            </Select>
            <Select
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
              size="small"
              sx={{
                minWidth: 90,
                backgroundColor: '#fff',
                height: '30px',
              }}>
              <MenuItem value="등록일">상담 작성일</MenuItem>
              <MenuItem value="출발일">여행 시작일</MenuItem>
            </Select>
            <IconButton
              onClick={fetchCounselingList}
              color="primary"
              size="small"
              sx={{
                p: 0.5,
                borderColor: 'primary.main',
                border: '1px solid',
                borderRadius: '4px',
                backgroundColor: '#fff',
                height: '30px',
                width: '30px',
              }}>
              <SearchIcon sx={{ fontSize: '20px' }} />
            </IconButton>
          </Box>
        </Paper>

        <Grid container spacing={1}>
          {filteredAndSortedData.map((counseling) => (
            <Grid item xs={12} key={counseling.상담번호} onClick={() => handleRowClick(counseling)}>
              <Card
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 3,
                    borderColor: 'primary.main',
                    borderWidth: 1,
                    borderStyle: 'solid',
                  },
                }}>
                <CardContent>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '16px' }}>
                        {counseling.견적명.toLocaleString()}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">
                        {counseling.출발일} ~ {counseling.종료일}
                      </Typography>
                      <Typography variant="body2">{counseling.인원수.toLocaleString()}인</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">{counseling.수배처}</Typography>
                      <Typography variant="body2">{counseling.상담상태}</Typography>
                      <Typography variant="body2">{counseling.요청자명}</Typography>
                    </Box>

                    {viewType === 'profit' ? (
                      <>
                        <Box sx={{ display: 'flex', gap: 3 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '50%' }}>
                            <Typography variant="body2" color="text.secondary">
                              판매총액
                            </Typography>
                            <Typography variant="body2">{counseling.판매총액.toLocaleString()}원</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '50%' }}>
                            <Typography variant="body2" color="text.secondary">
                              원가총액
                            </Typography>
                            <Typography variant="body2">{counseling.원가총액.toLocaleString()}원</Typography>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 3 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '50%' }}>
                            <Typography variant="body2" color="text.secondary">
                              예상수익
                            </Typography>
                            <Typography variant="body2">{counseling.예상총수익.toLocaleString()}원</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '50%' }}>
                            <Typography variant="body2" color="text.secondary">
                              미수
                            </Typography>
                            <Typography variant="body2">
                              {(counseling.판매총액 - counseling.입금총액).toLocaleString()}원
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 3 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '50%' }}>
                            <Typography variant="body2" color="text.secondary">
                              미지급
                            </Typography>
                            <Typography variant="body2">
                              {(counseling.원가총액 - counseling.출금총액).toLocaleString()}원
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '50%' }}>
                            <Typography variant="body2" color="text.secondary">
                              총수익
                            </Typography>
                            <Typography variant="body2" color="primary.main" sx={{ fontWeight: 'bold' }}>
                              {counseling.총수익.toLocaleString()}원
                            </Typography>
                          </Box>
                        </Box>
                      </>
                    ) : (
                      <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">
                            고객명:
                          </Typography>
                          <Typography variant="body2">{counseling.요청자명}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">
                            경로:
                          </Typography>
                          <Typography variant="body2">{counseling.경로}</Typography>
                        </Box>
                      </>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <SideMenu mobileOpen={mobileOpen} handleDrawerToggle={handleDrawerToggle} />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          p: 0,
          display: 'flex',
        }}>
        <Box sx={{ flex: 1 }}>{isMobile ? renderMobileContent() : renderPCContent()}</Box>
      </Box>
      <CounselingDetailDialog
        counselingNo={selectedCounseling}
        open={dialogVisible}
        onClose={(refresh) => {
          setDialogVisible(false);
          if (refresh) {
            fetchCounselingList();
          }
        }}
      />
      {currentCounselingData && (
        <AssignInfoDialog
          open={showAssignDialog}
          onClose={() => {
            setShowAssignDialog(false);
            setCurrentCounselingData(null);
          }}
          counselingDetail={currentCounselingData}
        />
      )}
    </Box>
  );
};

export default CounselingListPage;
